import React, { useState } from 'react';
import { Box, Button, Typography, Paper, CircularProgress, Alert, TextField } from '@mui/material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface StatsResponse {
  counts?: {
    total_associates: number;
    total_cvs: number;
    cvs_extracted: number;
    associates_with_engagements: number;
    total_engagements: number;
    organisations: number;
    countries_covered: number;
    iod_parc_engagements: number;
  };
  teamLeaders?: number;
  recentEngagements?: number;
  topSectors?: Array<{ sector: string; associates: number }>;
  topRegions?: Array<{ region: string; associates: number }>;
}

interface ExtractResponse {
  cvDocumentId?: number;
  associateId?: number;
  associateName?: string;
  filename?: string;
  engagementsExtracted?: number;
  engagementsNew?: number;
  engagementsMerged?: number;
  cost?: number;
  error?: string;
  // Batch response
  processed?: number;
  skipped?: number;
  failed?: number;
  totalCost?: number;
  totalEngagementsNew?: number;
  totalEngagementsMerged?: number;
  details?: Array<{
    cvDocumentId: number;
    filename: string;
    associateName: string;
    status: string;
    engagementsNew: number;
    engagementsMerged: number;
    cost: number;
    error?: string;
  }>;
}

const CompassTestPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [extractResult, setExtractResult] = useState<ExtractResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cvDocumentId, setCvDocumentId] = useState('1');
  const [batchLimit, setBatchLimit] = useState('5');

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE}/compass/stats`);
      setStats(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const extractSingle = async () => {
    setLoading(true);
    setError(null);
    setExtractResult(null);
    try {
      const response = await axios.post(`${API_BASE}/compass/extract`, {
        cvDocumentId: parseInt(cvDocumentId)
      });
      setExtractResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const extractBatch = async () => {
    setLoading(true);
    setError(null);
    setExtractResult(null);
    try {
      const response = await axios.post(`${API_BASE}/compass/extract`, {
        batch: true,
        limit: parseInt(batchLimit)
      });
      setExtractResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        🧭 Compass - BD Intelligence Test
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Test the Compass extraction and stats endpoints.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Stats</Typography>
        <Button 
          variant="contained" 
          onClick={fetchStats} 
          disabled={loading}
          sx={{ mb: 2 }}
        >
          {loading ? <CircularProgress size={20} /> : 'Fetch Stats'}
        </Button>

        {stats && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Overview:</Typography>
            <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, overflow: 'auto' }}>
              {JSON.stringify(stats, null, 2)}
            </pre>
          </Box>
        )}
      </Paper>

      {/* Single Extraction */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Extract Single CV</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            label="CV Document ID"
            value={cvDocumentId}
            onChange={(e) => setCvDocumentId(e.target.value)}
            size="small"
            type="number"
            sx={{ width: 150 }}
          />
          <Button 
            variant="contained" 
            color="primary"
            onClick={extractSingle} 
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Extract'}
          </Button>
        </Box>
      </Paper>

      {/* Batch Extraction */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Batch Extract</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            label="Limit"
            value={batchLimit}
            onChange={(e) => setBatchLimit(e.target.value)}
            size="small"
            type="number"
            sx={{ width: 100 }}
          />
          <Button 
            variant="contained" 
            color="secondary"
            onClick={extractBatch} 
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Extract Batch'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Extracts up to {batchLimit} pending CVs. Cost: ~$0.0024 per CV.
        </Typography>
      </Paper>

      {/* Extraction Result */}
      {extractResult && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Extraction Result</Typography>
          <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, overflow: 'auto', maxHeight: 400 }}>
            {JSON.stringify(extractResult, null, 2)}
          </pre>
        </Paper>
      )}
    </Box>
  );
};

export default CompassTestPage;
